// components/project/EconomicImportItemsModal.js
// Modal per importare voci capitolato/preventivo nel Quadro Economico (FASE 3-bis)
// Permette aggregazione per impresa/categoria
// Solo Admin/Administration

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { 
  Download, FileText, Loader2, AlertTriangle, CheckCircle, XCircle, 
  Info, Filter, Wand2, Building2, Package
} from 'lucide-react';
import api from '../../lib/api';

// Categorie economiche
const ECONOMIC_CATEGORIES = [
  { value: 'lavori_edili', label: 'Lavori Edili' },
  { value: 'impianti_idraulici', label: 'Impianti Idraulici' },
  { value: 'impianti_elettrici', label: 'Impianti Elettrici' },
  { value: 'cartongesso', label: 'Cartongesso' },
  { value: 'pitture_decorazioni', label: 'Pitture e Decorazioni' },
  { value: 'pavimenti_rivestimenti', label: 'Pavimenti e Rivestimenti' },
  { value: 'serramenti', label: 'Serramenti' },
  { value: 'forniture_prodotti', label: 'Forniture e Prodotti' },
  { value: 'climatizzazione', label: 'Climatizzazione' },
  { value: 'sicurezza', label: 'Sicurezza' },
  { value: 'arredi', label: 'Arredi' },
  { value: 'altro', label: 'Altro' }
];

// Tipi costo
const COST_TYPES = [
  { value: 'manodopera', label: 'Manodopera' },
  { value: 'materiale', label: 'Materiale' },
  { value: 'fornitura', label: 'Fornitura' },
  { value: 'subappalto', label: 'Subappalto' },
  { value: 'servizio', label: 'Servizio' },
  { value: 'tecnico', label: 'Tecnico' },
  { value: 'altro', label: 'Altro' }
];

// Formattazione valuta
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  const n0 = Number(value);
  const n = isNaN(n0) ? value : (Math.round(n0 * 100) === 0 ? 0 : n0);
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(n);
};

const EconomicImportItemsModal = ({ 
  isOpen, 
  onClose, 
  projectId,
  onImportComplete
}) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  
  // Selezioni
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // Form riga aggregata
  const [targetCategory, setTargetCategory] = useState('');
  const [description, setDescription] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [costType, setCostType] = useState('subappalto');
  
  // Filtri
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  
  // Carica voci disponibili
  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}/economic-import-items`);
      setItems(response.data.items || []);
      setSummary(response.data.summary || {});
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error fetching import items:', error);
      toast.error('Errore nel caricamento delle voci');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchItems();
      // Reset form
      setTargetCategory('lavori_edili');
      setDescription('');
      setSupplierName('');
      setCostType('subappalto');
      setFilterCategory('all');
      setFilterSource('all');
    }
  }, [isOpen, projectId, fetchItems]);

  // Voci filtrate
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (filterCategory !== 'all' && item.original_category !== filterCategory) return false;
      if (filterSource !== 'all' && item.source_type !== filterSource) return false;
      return true;
    });
  }, [items, filterCategory, filterSource]);

  // Voci disponibili (non già importate)
  const availableItems = useMemo(() => {
    return filteredItems.filter(item => !item.is_imported);
  }, [filteredItems]);

  // Toggle selezione singola
  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Seleziona tutti disponibili filtrati
  const selectAllFiltered = () => {
    const newSelected = new Set(selectedItems);
    availableItems.forEach(item => newSelected.add(item.item_id));
    setSelectedItems(newSelected);
  };

  // Deseleziona tutto
  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  // Preseleziona per categoria target
  const preselectForCategory = () => {
    if (!targetCategory) {
      toast.warning('Seleziona prima una categoria target');
      return;
    }
    
    const newSelected = new Set();
    availableItems.forEach(item => {
      if (item.suggested_category === targetCategory) {
        newSelected.add(item.item_id);
      }
    });
    
    if (newSelected.size === 0) {
      toast.info('Nessuna voce suggerita per questa categoria');
    } else {
      toast.success(`Preselezionate ${newSelected.size} voci suggerite`);
    }
    
    setSelectedItems(newSelected);
    
    // Imposta descrizione default
    const catLabel = ECONOMIC_CATEGORIES.find(c => c.value === targetCategory)?.label || targetCategory;
    setDescription(`${catLabel} - voci selezionate`);
  };

  // Calcola totale selezionato
  const selectedTotal = useMemo(() => {
    let total = 0;
    selectedItems.forEach(itemId => {
      const item = items.find(i => i.item_id === itemId);
      if (item) total += item.total_amount || 0;
    });
    return total;
  }, [selectedItems, items]);

  // Esegui import aggregato
  const handleImport = async () => {
    if (selectedItems.size === 0) {
      toast.warning('Seleziona almeno una voce');
      return;
    }
    
    if (!targetCategory) {
      toast.warning('Seleziona la categoria economica');
      return;
    }
    
    if (!description.trim()) {
      toast.warning('Inserisci una descrizione');
      return;
    }
    
    try {
      setImporting(true);
      
      const payload = {
        target_category: targetCategory,
        description: description.trim(),
        supplier_name: supplierName.trim() || null,
        assigned_company: supplierName.trim() || null,
        cost_type: costType,
        status: 'confermato',
        item_ids: Array.from(selectedItems)
      };
      
      const response = await api.post(`/projects/${projectId}/economic-import-items`, payload);
      const result = response.data;
      
      if (result.success) {
        toast.success(`Creata riga da ${result.imported_count} voci (${formatCurrency(result.total_amount)})`);
        
        // Ricarica e notifica
        await fetchItems();
        
        if (onImportComplete) {
          onImportComplete(result);
        }
        
        // Reset selezione
        setSelectedItems(new Set());
        
      } else {
        toast.error('Errore durante l\'import: ' + (result.errors?.join(', ') || 'Errore sconosciuto'));
      }
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImporting(false);
    }
  };

  // Categorie originali uniche per filtro
  const uniqueOriginalCategories = useMemo(() => {
    const cats = new Set(items.map(i => i.original_category).filter(Boolean));
    return Array.from(cats).sort();
  }, [items]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#FF7819]" />
            Importa voci capitolato
          </DialogTitle>
          <DialogDescription>
            Seleziona le voci dal preventivo/extra e crea una riga economica aggregata per impresa/categoria.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Caricamento voci...</span>
            </div>
          ) : (
            <>
              {/* Form riga aggregata */}
              <div className="grid grid-cols-4 gap-3 bg-gray-50 p-3 rounded-lg">
                <div>
                  <Label className="text-xs">Categoria economica *</Label>
                  <Select value={targetCategory} onValueChange={setTargetCategory}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Seleziona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ECONOMIC_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs">Descrizione riga *</Label>
                  <Input 
                    value={description} 
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Es: Lavori edili interni"
                    className="h-9"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Fornitore/Impresa</Label>
                  <Input 
                    value={supplierName} 
                    onChange={e => setSupplierName(e.target.value)}
                    placeholder="Es: Impresa Rossi S.r.l."
                    className="h-9"
                  />
                </div>
                
                <div>
                  <Label className="text-xs">Tipo costo</Label>
                  <Select value={costType} onValueChange={setCostType}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COST_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Filtri e azioni */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      {uniqueOriginalCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>Cat. {cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filterSource} onValueChange={setFilterSource}>
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue placeholder="Origine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutte</SelectItem>
                      <SelectItem value="estimate">Preventivo</SelectItem>
                      <SelectItem value="extra">Extra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={preselectForCategory}
                    disabled={!targetCategory}
                    className="h-8"
                  >
                    <Wand2 className="w-3 h-3 mr-1" />
                    Preseleziona suggerite
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectAllFiltered} className="h-8">
                    Seleziona tutto
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAll} className="h-8">
                    Deseleziona
                  </Button>
                </div>
              </div>

              {/* Tabella voci */}
              <div className="flex-1 overflow-auto border rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="w-16">Origine</TableHead>
                      <TableHead className="w-12">Cat.</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead className="w-16 text-right">Q.tà</TableHead>
                      <TableHead className="w-24 text-right">Prezzo</TableHead>
                      <TableHead className="w-24 text-right">Totale</TableHead>
                      <TableHead className="w-28">Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-gray-400 py-8">
                          Nessuna voce disponibile
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map(item => {
                        const isSelected = selectedItems.has(item.item_id);
                        const isSuggested = targetCategory && item.suggested_category === targetCategory;
                        
                        return (
                          <TableRow 
                            key={item.item_id}
                            className={`${item.is_imported ? 'opacity-50' : 'cursor-pointer hover:bg-gray-50'} ${isSelected ? 'bg-orange-50' : ''} ${isSuggested && !item.is_imported ? 'border-l-2 border-l-orange-400' : ''}`}
                            onClick={() => !item.is_imported && toggleItem(item.item_id)}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={isSelected}
                                disabled={item.is_imported}
                                onCheckedChange={() => toggleItem(item.item_id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]">
                                {item.source_type === 'estimate' ? 'PREV' : 'EXTRA'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {item.original_category || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="text-sm truncate max-w-[250px]" title={item.description}>
                                  {item.description}
                                </span>
                                {isSuggested && !item.is_imported && (
                                  <Badge className="bg-orange-100 text-orange-700 text-[9px] px-1">
                                    Suggerita
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {item.quantity > 0 ? `${item.quantity} ${item.unit || ''}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              {formatCurrency(item.unit_price)}
                            </TableCell>
                            <TableCell className="text-right font-medium text-sm">
                              {formatCurrency(item.total_amount)}
                            </TableCell>
                            <TableCell>
                              {item.is_imported ? (
                                <Badge className="bg-gray-100 text-gray-500 text-[10px]">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Già importata
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Disponibile
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Riepilogo selezione */}
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-500">
                    Voci totali: <strong>{summary?.total_items || 0}</strong>
                  </span>
                  <span className="text-gray-500">
                    Disponibili: <strong className="text-green-600">{summary?.available_items || 0}</strong>
                  </span>
                  <span className="text-gray-500">
                    Già importate: <strong className="text-gray-400">{summary?.imported_items || 0}</strong>
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Selezionate</div>
                    <div className="text-lg font-bold text-[#FF7819]">{selectedItems.size} voci</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Totale vendita</div>
                    <div className="text-lg font-bold text-green-600">{formatCurrency(selectedTotal)}</div>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 p-3 rounded">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-700">Come funziona:</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    <li>Seleziona la categoria economica e clicca "Preseleziona suggerite" per un aiuto automatico</li>
                    <li>Le voci selezionate verranno aggregate in UNA riga economica</li>
                    <li>Il totale vendita sarà la somma delle voci selezionate</li>
                    <li>Costi stimati e consuntivi saranno da compilare manualmente</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={importing}
          >
            Annulla
          </Button>
          <Button 
            onClick={handleImport}
            disabled={importing || selectedItems.size === 0 || !targetCategory || !description.trim()}
            className="bg-[#FF7819] hover:bg-[#e56a10]"
          >
            {importing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importazione...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Crea riga aggregata ({selectedItems.size} voci)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EconomicImportItemsModal;
